import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useCallback } from 'react';

import { userIdAtom } from '@/services/globalState';
import {
  applyCalloutAction,
  getUserCalloutsQueryKey,
  type CalloutKey,
  type UserCallout,
} from '@/services/userCallouts';

/**
 * The shared optimistic `resolve` transition for user callouts: records a
 * terminal `resolve` for a key so the callout stays gone across the user's
 * devices, and optimistically resolves the cached row so a banner can't flash
 * back before the refetch lands (rolling back on error). Split out so every
 * callout banner — the single-key `useCalloutBanner` and the coordinated
 * `useComposerCalloutBanner` — shares one implementation of the optimistic path
 * instead of duplicating it.
 *
 * Returns a `resolve(key)` callback; the key is supplied at call time so one
 * mounted hook can resolve whichever callout is active.
 */
export function useResolveCallout() {
  const userId = useAtomValue(userIdAtom);

  const queryClient = useQueryClient();

  const resolveMutation = useMutation({
    mutationFn: (key: CalloutKey) => applyCalloutAction(key, 'resolve'),
    onMutate: async (key: CalloutKey) => {
      const queryKey = getUserCalloutsQueryKey(userId);

      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<UserCallout[]>(queryKey);

      queryClient.setQueryData<UserCallout[]>(queryKey, (current = []) => {
        const now = new Date().toISOString();

        const existing = current.find((callout) => callout.key === key);

        const base: UserCallout = existing ?? {
          key,
          shownCount: 0,
          lastShownAt: null,
          dismissedCount: 0,
          lastDismissedAt: null,
          deferredUntil: null,
          resolvedAt: null,
          context: null,
          createdAt: now,
          updatedAt: now,
        };

        const others = current.filter((callout) => callout.key !== key);

        return [...others, { ...base, resolvedAt: now }];
      });

      return { previous };
    },
    onError: (_error, _key, context) => {
      if (context?.previous) {
        queryClient.setQueryData(getUserCalloutsQueryKey(userId), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: getUserCalloutsQueryKey(userId) });
    },
  });

  return useCallback((key: CalloutKey) => resolveMutation.mutate(key), [resolveMutation]);
}
